import { useState, useMemo } from 'react'
import type { SavedSunoPrompt } from '@creanizer/types'
import { Card, CardContent, CardHeader, CardTitle } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { ScrollArea } from '@creanizer/ui'
import { Separator } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Search, Star, StarOff, Trash2, FolderOpen, Calendar, Music } from 'lucide-react'

interface SavedSunoPromptListProps {
  prompts: SavedSunoPrompt[]
  onLoad: (prompt: SavedSunoPrompt) => void
  onDelete: (id: string) => void
  onToggleFavorite: (prompt: SavedSunoPrompt) => void
}

export function SavedSunoPromptList({
  prompts,
  onLoad,
  onDelete,
  onToggleFavorite,
}: SavedSunoPromptListProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<'all' | 'favorites' | 'vocal' | 'instrumental'>('all')

  const filteredPrompts = useMemo(() => {
    let filtered = prompts

    // タイプフィルター
    switch (filterType) {
      case 'favorites':
        filtered = filtered.filter(p => p.favorite)
        break
      case 'vocal':
        filtered = filtered.filter(p => p.result.promptInfo.type === 'vocal')
        break
      case 'instrumental':
        filtered = filtered.filter(p => p.result.promptInfo.type === 'instrumental')
        break
    }

    // 検索フィルター
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(p =>
        p.title.toLowerCase().includes(query) ||
        p.result.promptInfo.genre?.toLowerCase().includes(query) ||
        p.result.promptInfo.summary?.toLowerCase().includes(query) ||
        p.tags?.some(tag => tag.toLowerCase().includes(query))
      )
    }

    // 最新順でソート
    return filtered.sort((a, b) =>
      new Date(b.updatedAt || b.createdAt).getTime() -
      new Date(a.updatedAt || a.createdAt).getTime()
    )
  }, [prompts, searchQuery, filterType])

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'vocal':
        return '🎤'
      case 'instrumental':
        return '🎹'
      default:
        return '🎵'
    }
  }

  if (prompts.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Music className="w-12 h-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground text-center">
            まだ保存されたプロンプトはありません
            <br />
            生成したプロンプトを保存してください
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* 検索とフィルター */}
      <Card>
        <CardHeader>
          <CardTitle>保存済みプロンプト</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="タイトル、ジャンル、タグで検索..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>

          <Tabs value={filterType} onValueChange={(value: any) => setFilterType(value)}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="all">
                すべて ({prompts.length})
              </TabsTrigger>
              <TabsTrigger value="favorites">
                お気に入り ({prompts.filter(p => p.favorite).length})
              </TabsTrigger>
              <TabsTrigger value="vocal">
                ボーカル ({prompts.filter(p => p.result.promptInfo.type === 'vocal').length})
              </TabsTrigger>
              <TabsTrigger value="instrumental">
                インスト ({prompts.filter(p => p.result.promptInfo.type === 'instrumental').length})
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </CardContent>
      </Card>

      {/* プロンプトリスト */}
      <Card>
        <CardContent className="p-0">
          <ScrollArea className="h-[600px]">
            {filteredPrompts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <p className="text-muted-foreground">
                  条件に一致するプロンプトが見つかりません
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredPrompts.map((prompt) => (
                  <div
                    key={prompt.id}
                    className="p-4 hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-start gap-2">
                          <span className="text-lg">{getTypeIcon(prompt.result.promptInfo.type)}</span>
                          <div className="flex-1">
                            <h3 className="font-semibold">{prompt.title}</h3>
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {prompt.result.promptInfo.summary}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline">
                            {prompt.result.promptInfo.genre}
                          </Badge>
                          <Badge variant="secondary">
                            {prompt.result.promptInfo.characterCount}文字
                          </Badge>
                          <Badge variant={
                            prompt.result.promptInfo.estimatedQuality === '高' ? 'default' :
                            prompt.result.promptInfo.estimatedQuality === '中' ? 'secondary' :
                            'destructive'
                          }>
                            {prompt.result.promptInfo.estimatedQuality}品質
                          </Badge>
                          {prompt.tags?.map((tag, index) => (
                            <Badge key={index} variant="outline">
                              {tag}
                            </Badge>
                          ))}
                        </div>

                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(prompt.updatedAt || prompt.createdAt)}
                          </span>
                          {prompt.worldId && (
                            <Badge variant="outline" className="text-xs">
                              世界観連携
                            </Badge>
                          )}
                          {prompt.characterId && (
                            <Badge variant="outline" className="text-xs">
                              キャラ連携
                            </Badge>
                          )}
                          {prompt.lyricsId && (
                            <Badge variant="outline" className="text-xs">
                              歌詞連携
                            </Badge>
                          )}
                        </div>

                        {prompt.memo && (
                          <p className="text-sm text-muted-foreground italic">
                            📝 {prompt.memo}
                          </p>
                        )}
                      </div>

                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          onClick={() => onLoad(prompt)}
                        >
                          <FolderOpen className="w-4 h-4 mr-2" />
                          読み込み
                        </Button>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onToggleFavorite(prompt)}
                          >
                            {prompt.favorite ? (
                              <Star className="w-4 h-4 fill-current text-yellow-500" />
                            ) : (
                              <StarOff className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              if (confirm('このプロンプトを削除してもよろしいですか？')) {
                                onDelete(prompt.id)
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* 使用履歴 */}
                    {prompt.usageHistory && prompt.usageHistory.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs text-muted-foreground mb-1">使用履歴:</p>
                        <div className="flex gap-2">
                          {prompt.usageHistory.slice(-3).map((history, index) => (
                            <Badge
                              key={index}
                              variant={
                                history.result === 'success' ? 'default' :
                                history.result === 'partial' ? 'secondary' :
                                'destructive'
                              }
                              className="text-xs"
                            >
                              {new Date(history.usedAt).toLocaleDateString()}
                              {history.notes && ` - ${history.notes}`}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  )
}